/**
 * "This month" — `client/widget-chatbot.html` §2 and `client/widget-booking.html` §2.
 *
 * Two mockups, one panel, one route (`/app/widgets/$widgetRef`): the chatbot and the
 * booking widget differ in their unit noun, their numbers and their prose, and in
 * nothing this component decides. Everything it prints comes from
 * `WidgetPageSchema` — the summary the API already computed and the series it
 * already grouped — so a third widget type needs no code
 * (`usage.test.ts` §"adding a widget type" makes the same promise on the API side).
 *
 * **The two series are not the same shape, and that is the rule this file is
 * written against.** The chatbot's is 24 daily points; the booking widget's is four
 * weekly buckets (92/108/101/88). Anything divided by `series.days.length` is
 * therefore "a day" for one widget and a lie for the other — which is exactly how a
 * "daily average 97" reached a page whose own mockup draws no average at all. The
 * panel states the **total** and the **period** and divides by nothing.
 *
 * Three things it deliberately does not do:
 *
 *   - **It computes no money.** `amount`, `projected_amount` and `worst_case` are the
 *     read model's; the cap sentence names `worst_case` rather than multiplying
 *     `(cap − included) × unit_amount` in the browser.
 *   - **It draws the meter from quantities**, never from a width. `Meter` takes
 *     `used`/`included`/`cap` and owns the geometry (`meterGeometry`).
 *   - **It states the conclusion on the chart.** CONTRACT.md §4.7 makes
 *     `aria-label` + a table view a hard requirement, and `Chart` refuses to render
 *     without both.
 *
 * One quantity is still arithmetic here and is meant to be: `cap − included`, the
 * "2,000" and the "700" both mockups print in the cap sentence. `cap_headroom` on
 * `WidgetSummarySchema` is the field that would retire it; until then, dropping the
 * expression would delete a published fact. `peak` is the other, and it is chart
 * geometry rather than a fact — see its own comment.
 *
 * **A widget not charged per unit has no allowance and no cap** (session 46c — the
 * blog at $5.00 a month, the forms free). `pricing.isMetered` says which, and such a
 * widget gets no meter, no included or cap rows, no projected charge and no cap
 * sentence: its price is stated once, in `format/widgetPrice.ts`'s words, with the
 * month its fee starts in when it has one. The count and the chart stay — every post
 * and every lead is still counted, and that is what the page is for.
 */

import {
  isMetered,
  isZero,
  sentenceCase,
  type UsageSeriesView,
  type WidgetSummaryView,
  type WidgetView,
} from '@fareast/shared'
import type { ReactNode } from 'react'

import { Chart, ColumnChart, COLUMN_VIEW_BOX, roundAxisTicks } from '../portal/Chart.js'
import { Kv, KvRow } from '../portal/Kv.js'
import { Meter } from '../portal/Meter.js'
import { RegionCols } from '../portal/Region.js'
import { Push, Row, Stack } from '../portal/Layout.js'
import { Answer, Muted, Num, Prose, Small } from '../portal/Text.js'
import { Tag } from '../portal/Status.js'
import { useFormat, type Formatter } from '../../format/useFormat.js'
import { monthlyFeeWords, widgetPriceWords } from '../../format/widgetPrice.js'
import { pluralOf } from './unitNoun.js'

export type WidgetUsagePanelProps = {
  readonly widget: WidgetView
  readonly summary: WidgetSummaryView
  readonly series: UsageSeriesView
  /** The sentence above the columns. The page writes it; it is the page's answer. */
  readonly answer?: string
  /** The buttons under the meter: pause/resume and "Change the cap". */
  readonly actions?: ReactNode
  /**
   * The last line under the meter. Defaults to the widget's reference.
   *
   * Both mockups print something else beside it — "API key rotated 2 Jun 2026" on
   * the chatbot, "Installed as part of the website build" on the booking widget —
   * and both are fields on `WidgetView`. Which one matters is the page's to say, so
   * the page says it.
   */
  readonly foot?: ReactNode
}

/**
 * Up to this many points, every column is labelled.
 *
 * `widget-booking.html` labels all four of its weekly buckets; a shared constant of
 * 5 gave that series **one** label, because `ColumnChart` draws a label when
 * `index % every === 0` and four points hit that once.
 */
const LABEL_EVERY_POINT_UPTO = 8

/**
 * Past that, at most this many labels, evenly spaced.
 *
 * Five over 24 days is `1 · 6 · 11 · 16 · 21`, which is the mockup's own spacing.
 * The mockup also labels the **last** column (`24`) and this cannot: `ColumnChart`
 * has no "and the last one always does", and `components/portal/Chart.tsx` is not
 * this session's to edit. Reported to the lead as the primitive change it needs.
 */
const MAX_X_LABELS = 5

/**
 * How often a column gets an x label, from how many columns there are.
 *
 * The smallest interval that keeps the count at or under `MAX_X_LABELS`, found by
 * counting up rather than by `Math.ceil`: `Math.round`/`ceil`/`floor`/`trunc` are
 * lint-banned outside `money.ts` and `dates.ts` (`eslint.config.js`), including in
 * a component, and the bound here is at most a month of days.
 */
function labelEveryFor(points: number): number {
  if (points <= LABEL_EVERY_POINT_UPTO) return 1
  let every = 1
  while (points > every * MAX_X_LABELS) every += 1
  return every
}

export function WidgetUsagePanel({
  widget,
  summary,
  series,
  answer,
  actions,
  foot,
}: WidgetUsagePanelProps) {
  const format = useFormat()
  const noun = summary.unit_noun
  const plural = pluralOf(noun)
  const month = format.monthLong(series.period)

  /*
   * The top of the y axis, and the one number this component works out for itself.
   * It is geometry rather than a fact: nothing on screen prints it, `roundAxisTicks`
   * only needs to know how tall to draw, and a `peak` field on the series would be a
   * schema column in service of an axis. Every *quantity* the panel prints comes off
   * the summary.
   */
  const peak = series.days.reduce((most, day) => Math.max(most, day.qty), 0)

  /*
   * The conclusion CONTRACT.md §4.7 requires, and it names only what the series
   * carries: how many, of what, in which month. It used to end "averaging N a day",
   * computed as `total / days.length` — true of a daily series and false of the
   * booking widget's weekly one, in the same sentence a screen reader is given as
   * the whole of the chart.
   */
  const conclusion = `${widget.name}: ${format.number(series.total)} ${plural} in ${month}.`

  return (
    <>
      {answer === undefined ? null : <Answer>{answer}</Answer>}

      <RegionCols>
        <Stack gap="tight">
          <Chart
            conclusion={conclusion}
            viewBox={COLUMN_VIEW_BOX}
            table={{
              /*
               * Both mockups close this table with a total row — "1–24 Aug · 1,264"
               * and "Total · 389" — and `TableView` (`components/portal/Chart.tsx`)
               * has no facility for one: every row is a `<td>`, and `.is-total` is
               * the class it would need. Reported to the lead. Adding the total as
               * an ordinary row would print it as one more day, which is worse than
               * leaving it to the conclusion above.
               */
              columns: [{ label: month }, { label: sentenceCase(plural), right: true }],
              rows: series.days.map((day) => [format.day(day.on), format.number(day.qty)]),
            }}
          >
            <ColumnChart
              data={series.days.map((day) => ({
                label: String(dayOfMonth(day.on)),
                value: day.qty,
                title: `${format.day(day.on)}: ${format.number(day.qty)} ${plural}`,
              }))}
              ticks={roundAxisTicks(peak, (value) => format.number(value))}
              labelEvery={labelEveryFor(series.days.length)}
            />
          </Chart>
        </Stack>

        {isMetered(summary.unit_amount.amount) ? (
          <MeteredFacts widget={widget} summary={summary} actions={actions} foot={foot} />
        ) : (
          <UnmeteredFacts widget={widget} summary={summary} actions={actions} foot={foot} />
        )}
      </RegionCols>
    </>
  )
}

type FactsProps = {
  readonly widget: WidgetView
  readonly summary: WidgetSummaryView
  readonly actions: ReactNode
  readonly foot: ReactNode
}

/**
 * The right-hand column for a widget charged per unit — both mockups' own, figure for
 * figure: the meter, the unit price, the allowance, the cap, the most the cap lets it
 * bill, and the projection. A fee beside the unit price (nothing seeded carries both) is one more
 * row, in the words every page uses for it.
 */
function MeteredFacts({ widget, summary, actions, foot }: FactsProps) {
  const format = useFormat()
  const noun = summary.unit_noun
  const plural = pluralOf(noun)

  return (
    <Stack>
      {/*
       * The label is the noun and the top is `1,264 / 1,000`.
       *
       * `widget-chatbot.html` writes "Against the 1,000 included" over a top of
       * "1,264"; `Meter` prints `used / included` itself, so keeping the
       * mockup's label would say "1,000" twice — which is the chrome P7
       * deletes. Same two facts, one statement of each.
       *
       * **No `cap` is passed, on purpose.** `meterGeometry` scales the track to
       * `max(included, used, cap)`, so handing it the chatbot's 3,000 would drop
       * the fill from the mockup's 79.1% to 33.3% and put the whole month in the
       * left third of the track. The cap is a row below, beside the most it
       * lets the widget bill.
       */}
      <Meter
        label={`${sentenceCase(plural)} this month`}
        used={summary.used}
        included={summary.included}
        foot={
          summary.billable > 0
            ? `${format.number(summary.billable)} over the allowance, and billable`
            : 'Nothing is over the allowance, so nothing this month is billable'
        }
      />

      <Kv bare>
        {/*
         * Sentence-cased, like the meter label and the table header above it.
         * Both mockups write a definition here — "Conversation — one visitor
         * thread, not one message", "Booking — a confirmed slot, not a page
         * view" — and the second half of each is `widget_type.unit_description`,
         * a field that does not exist. Reported.
         */}
        <KvRow k="Unit" v={sentenceCase(noun)} />
        {/*
         * `$0.12` and `pilot` are two nodes, and two adjacent JSX elements on
         * their own lines have no whitespace between them — so the row read
         * `$0.12pilot`, one word, on both widget pages. The mockup writes
         * `<span class="v num">$0.12 <span class="tag">pilot</span></span>`
         * and gets its space from the source text; the fix here is the
         * primitive that owns "two things side by side with `--sp-2` between
         * them" (CONTRACT.md §2.4 `.row`) rather than an `&nbsp;` or a margin
         * on `Tag`, which would follow the tag to every other place it is used.
         *
         * `Push` first, because `.kv dd` is right-aligned and a `.row` inside it
         * is a full-width flex container that would otherwise pack the pair to
         * the left of a column of figures that all end at the same edge.
         */}
        <KvRow
          k="Unit price"
          v={
            <Row>
              <Push />
              <Num value={summary.unit_amount} />
              {widget.pilot ? <Tag>pilot</Tag> : null}
            </Row>
          }
        />
        {isZero(summary.monthly_amount) ? null : (
          <KvRow k="Monthly fee" v={<Num>{monthlyFeeWords(summary.monthly_amount, format)}</Num>} />
        )}
        <KvRow k="Included / mo" v={<Num value={summary.included} />} />
        <KvRow k="Cap / mo" v={<Num value={summary.cap} />} />
        <KvRow k="Most it can bill / mo" v={<Num value={summary.worst_case} />} />
        {/*
         * `≈`, as both mockups write it — "≈ 1,630", "≈ $80.00", and
         * `projected_qty`'s own schema description quotes the first of them. A
         * projection printed as a bare figure reads as a fact, and this one is
         * a forecast off `days_elapsed`.
         */}
        <KvRow k="Projected month end" v={<Num>≈ {format.number(summary.projected_qty)}</Num>} />
        <KvRow k="Projected charge" v={<Num>≈ {format.money(summary.projected_amount)}</Num>} />
      </Kv>

      <Small>Past the cap it stops serving and your site shows its fallback.</Small>

      {actions}

      <Small>
        <Muted>{foot ?? widget.ref}</Muted>
      </Small>
    </Stack>
  )
}

/**
 * The right-hand column for a widget that is not charged per unit (session 46c).
 *
 * The price, once, in `widgetPriceWords` — "$5.00 a month" or "Free" — and, for a fee,
 * the month it starts in and the rule it bills by. No meter: there is no allowance to
 * fill, and a bar against zero would read as a limit the widget does not have.
 */
function UnmeteredFacts({ widget, summary, actions, foot }: FactsProps) {
  const format = useFormat()
  const noun = summary.unit_noun

  return (
    <Stack>
      <Kv bare>
        <KvRow k="Unit" v={sentenceCase(noun)} />
        <KvRow
          k="Price"
          v={
            <Row>
              <Push />
              <Num>{widgetPriceWords(widget, format)}</Num>
              {widget.pilot ? <Tag>pilot</Tag> : null}
            </Row>
          }
        />
        {widget.fee_starts_in === null ? null : (
          <KvRow k="Billed from" v={format.monthLong(widget.fee_starts_in)} />
        )}
      </Kv>

      <Prose>{unmeteredSentence(widget, noun, format)}</Prose>

      {actions}

      <Small>
        <Muted>{foot ?? widget.ref}</Muted>
      </Small>
    </Stack>
  )
}

/** What a widget with no unit price costs, as the sentence under its price. */
function unmeteredSentence(widget: WidgetView, noun: string, format: Formatter): string {
  if (isZero(widget.monthly_amount)) {
    return (
      `Nothing is charged for this widget. Every ${noun} is counted, so you can see how ` +
      'many there were, and none is ever billed.'
    )
  }
  return (
    `Nothing is charged per ${noun}. The widget is ${monthlyFeeWords(widget.monthly_amount, format)}, ` +
    'billed in arrears on the 1st for each full calendar month it is live — its first, partial ' +
    'month is free, and a month it is paused when the month closes is not billed.'
  )
}

/** `2026-08-09` → `9`. The column label is the day of the month, as the mockup draws. */
function dayOfMonth(on: string): number {
  return Number(on.slice(-2))
}
